import type { Directive } from 'vue'

type RevealDirection = 'up' | 'left' | 'right'

const observers = new WeakMap<HTMLElement, IntersectionObserver>()

/**
 * v-reveal / v-reveal="'left'" — fade an element in the first time it scrolls into view.
 * Stagger siblings by setting `transition-delay` inline on each element.
 * Reduced motion is handled by the global CSS media query in style.css.
 */
export const reveal: Directive<HTMLElement, RevealDirection | undefined> = {
  mounted(el, binding) {
    el.dataset.reveal = binding.value ?? 'up'
    if (!('IntersectionObserver' in window)) {
      el.classList.add('is-revealed')
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue
          el.classList.add('is-revealed')
          observer.disconnect()
          observers.delete(el)
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' },
    )
    observer.observe(el)
    observers.set(el, observer)
  },
  unmounted(el) {
    observers.get(el)?.disconnect()
    observers.delete(el)
  },
}
